<template lang="pug">
    v-form.c-form(autocomplete="off" @submit.prevent="submit")

        //  Name field
        v-text-field(
            :error-messages="errors.collect('fullName')"
            :data-vv-as="$t('fields.fullName')"
            :label="$t('fields.fullName')"
            name="fullName"
            v-model="model.fullName"
            v-validate="'required|min:2|max:72'"
            outline
        )

        // Email field
        v-text-field(
            name="email"
            :label="$t('fields.email')"
            type="email"
            v-model="model.email"
            v-validate="{ required: true, email: true }"
            :data-vv-as="$t('fields.email')"
            :error-messages="errors.collect('email')"
            @blur="validateEmail"
            browser-autocomplete="new-password"
            outline
        )

        // Contact Phone field
        v-text-field(
            :error-messages="errors.collect('phone')"
            :data-vv-as="$t('fields.phoneNumber')"
            :label="$t('fields.phoneNumber')"
            name="phone"
            v-model="model.phone"
            v-validate="'required|min:6'"
            mask="###-###-######-####"
            outline
        )

        // Password field
        v-text-field(
            name="password"
            :label="$t('fields.password')"
            v-model="model.password"
            v-validate="{ required: true, min: 6, max: 24, verify_password: true }"
            :data-vv-as="$t('fields.password')"
            :error-messages="errors.collect('password')"
            browser-autocomplete="new-password"
            outline
            ref="password"
            :append-icon="visibilityPassword ? 'visibility' : 'visibility_off'"
            @click:append="() => (visibilityPassword = !visibilityPassword)"
            :type="visibilityPassword ? 'text' : 'password'"
        )

        // Password Confirm field
        v-text-field(
            name="passwordConfirmation"
            v-model="model.passwordConfirmation"
            :label="$t('fields.confirm_password')"
            v-model.lazy="model.passwordConfirmation"
            v-validate="{ required: true, min: 6, max:24, is: model.password, verify_password: true }"
            :data-vv-as="$t('fields.confirm_password')"
            :error-messages="errors.collect('passwordConfirmation')"
            outline
            data-vv-as="Confirm Password"
            :append-icon="visibilityPasswordConfirmation ? 'visibility' : 'visibility_off'"
            @click:append="() => (visibilityPasswordConfirmation = !visibilityPasswordConfirmation)"
            :type="visibilityPasswordConfirmation ? 'text' : 'password'"
        )

        div(v-if="isPublic")
            separator-component

            div.mb-3(v-html="$t('auth.registration.messagePublic')")

            // Company field
            v-text-field(
                v-model="model.department"
                name="company"
                :label="$t('fields.organizationName')"
                :data-vv-as="$t('fields.company')"
                :error-messages="errors.collect('company')"
                outline
            )

            // Position
            v-text-field(
                name="position"
                :label="$t('fields.positionTitle')"
                v-model="model.businessPosition"
                outline
                :error-messages="errors.collect('position')"
                :data-vv-as="$t('fields.position')"
                v-validate="'min:2|max:100'"
            )

            // Company Size
            v-select(
                :items="$t('selection.companySizes')"
                :label="$t('fields.organizationsSize')"
                name="companySize"
                v-model="model.businessSize"
                append-icon="expand_more"
                outline
                :data-vv-as="$t('fields.companySize')"
                :error-messages="errors.collect('companySize')"
            )

        div
            div.text-xs-right

                // Submit button
                button-component.mx-0.full-width(
                    large
                    type="submit"
                    color="primary"
                    size="medium"
                    :action="submit"
                    :isLoading="isLoading"
                ) {{ $t('auth.registration.confirm') }}

            separator-component

            div
                // Redirect to login page
                | {{ $t('auth.registration.alreadyAMember') }} &nbsp;
                router-link.link(a to="/login") {{ $t('auth.registration.signIn') }}



</template>

<script>
    import Button from '~components/Button';
    import Separator from '~components/Separator';
    import { mapActions, mapGetters } from 'vuex';
    import auth from '~api/auth';

    export default {
        $_veeValidate: {
            validator: 'new'
        },
        components: {
            ButtonComponent: Button,
            SeparatorComponent: Separator
        },
        data() {
            return {
                model: {
                    fullName: '',
                    email: '',
                    department: null,
                    businessSize: null,
                    businessPosition: null,
                    phone: '',
                    password: '',
                    passwordConfirmation: ''
                },
                isPublic: false,
                visibilityPassword: false,
                visibilityPasswordConfirmation: false,
                companies: [],
                selectSearch: null,
                loading: false
            };
        },
        computed: {
            ...mapGetters('auth', ['isLoading'])
        },
        mounted() {
            const dict = {
                custom: {
                    fullName: {
                        required: 'Full Name field is required',
                        min: 'Your Full Name must be at least 2 characters.',
                        max:
                            'Your Full Name may not be greater than 72 characters'
                    },
                    phone: {
                        required: 'Phone Number field is required',
                        min: 'Your Phone Number must be at least 6 characters.'
                    },
                    email: {
                        email: 'Invalid Email',
                        required: 'Email field is required'
                    },
                    password: {
                        required: 'Password field is required',
                        min: 'Your Password must be at least 6 characters.',
                        max:
                            'Your Password may not be greater than 24 characters'
                    },
                    passwordConfirmation: {
                        required: 'Confirm Password field is required',
                        is: 'Oops! It looks like your Password doesn’t match.',
                        min: 'Your Password must be at least 6 characters.',
                        max:
                            'Your Password may not be greater than 24 characters'
                    }
                }
            };

            this.$validator.localize('en', dict);
        },
        methods: {
            ...mapActions('auth', ['confirmPassword', 'registration']),

            /**
             * Create user and redirect to confirmation registration page
             */
            async submit() {
                const validation = await this.$validator.validateAll();
                if (!validation) return;

                this.registration({
                    model: this.model,
                    isPublic: this.isPublic
                });
            },

            async validateEmail() {
                if (this.model.email.length < 5) return;

                const resp = await auth.validateEmail({
                    email: this.model.email
                });
                if (resp.ok) {
                    this.department = null;
                    this.businessSize = null;
                    this.businessPosition = null;
                    return (this.isPublic = false);
                }

                return (this.isPublic = true);
            }
        }
    };
</script>

<style lang="stylus" scoped>
    .link
        font-size 14px;
</style>
